import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '../store/authStore'
import toast from 'react-hot-toast'
import { normalizeApiPath } from '../utils/apiPath'

const API_BASE = '/api/v1'

function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return host.includes('localhost') || host.includes('127.0.0.1')
}

/** Producción HTTPS o dominio remoto: siempre rutas relativas (mismo origen). */
function mustUseRelativeApi(): boolean {
  if (typeof window === 'undefined') return true
  if (window.location.protocol === 'https:') return true
  return !isLocalhost()
}

function resolveBaseUrl(): string {
  if (mustUseRelativeApi()) {
    return API_BASE
  }

  const envUrl = (import.meta as any).env?.VITE_API_URL as string | undefined
  if (envUrl && (envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))) {
    return envUrl
  }

  return API_BASE
}

function stripToRelativePath(url: string): string {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return url
  }
  try {
    const parsed = new URL(url)
    return parsed.pathname + (parsed.search || '')
  } catch {
    const match = url.match(/\/api\/v1(\/.*)?(\?.*)?$/)
    return match ? `/api/v1${match[1] || ''}${match[2] || ''}` : API_BASE
  }
}

/** Normaliza path antes de cada petición (evita /api/v1/api/v1/... con builds antiguos). */
function resolveRequestUrl(url: string): string {
  return normalizeApiPath(stripToRelativePath(url), API_BASE)
}

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: resolveBaseUrl(),
      timeout: 10000,
      maxRedirects: 0,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private sanitizeRequestConfig(config: AxiosRequestConfig): AxiosRequestConfig {
    if (mustUseRelativeApi()) {
      config.baseURL = API_BASE
    } else if (config.baseURL) {
      const base = String(config.baseURL)
      if (base.startsWith('http://') || base.startsWith('https://')) {
        config.baseURL = stripToRelativePath(base) || API_BASE
      }
    }

    if (config.url) {
      config.url = stripToRelativePath(config.url)
      const base = String(config.baseURL || API_BASE)
      config.url = normalizeApiPath(config.url, base)
    }

    return config
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(
      (config) => {
        this.sanitizeRequestConfig(config)

        try {
          const token = useAuthStore.getState().token
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        } catch (error) {
          console.warn('Error getting token from store:', error)
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        if (
          error.response?.status === 301 ||
          error.response?.status === 302 ||
          error.response?.status === 307 ||
          error.response?.status === 308
        ) {
          const location = error.response.headers.location
          if (location) {
            try {
              const url = new URL(location, window.location.origin)
              const originalRequest = error.config
              originalRequest.url = normalizeApiPath(
                stripToRelativePath(url.pathname + url.search),
                API_BASE
              )
              originalRequest.baseURL = API_BASE
              return this.api.request(originalRequest)
            } catch {
              // continuar con manejo normal
            }
          }
        }

        const requestUrl = error.config?.url || ''
        const isAuthMe = requestUrl.includes('/auth/me')
        const isPublicEndpoint =
          requestUrl.includes('-public') ||
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/token')

        if (error.response?.status === 401 || (error.response?.status === 403 && isAuthMe)) {
          if (!isPublicEndpoint) {
            useAuthStore.getState().clearAuth()
            toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.')
            window.location.href = '/login'
          }
        } else if (error.response?.status === 403) {
          toast.error('No tienes permisos para realizar esta acción')
        } else if (error.response?.status >= 500) {
          toast.error('Error del servidor. Intenta nuevamente.')
        } else if (error.code === 'ECONNABORTED') {
          toast.error('Tiempo de espera agotado. Verifica tu conexión.')
        } else if (!error.response) {
          toast.error('Error de conexión. Verifica tu internet.')
        }

        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(resolveRequestUrl(url), config)
    return response.data
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(resolveRequestUrl(url), data, config)
    return response.data
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(resolveRequestUrl(url), data, config)
    return response.data
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(resolveRequestUrl(url), data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(resolveRequestUrl(url), config)
    return response.data
  }

  async upload<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(resolveRequestUrl(url), formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    })
    return response.data
  }
}

export const apiService = new ApiService()
